import { useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/feature/TopBar";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { SHOW_REVIEWS } from "@/config/features";
import { PHONE } from "@/mocks/updiag";

const typeOptions = [
  { value: "appartement", label: "Appartement", icon: "ri-building-2-line" },
  { value: "maison", label: "Maison", icon: "ri-home-2-line" },
  { value: "local", label: "Local commercial", icon: "ri-store-2-line" },
  { value: "autre", label: "Autre bien", icon: "ri-building-line" },
];

const diagnosticOptions = [
  { value: "dpe", label: "DPE — Diagnostic de Performance Énergétique", icon: "ri-fire-line", price: "Dès 130 €" },
  { value: "pack-vente", label: "Pack Vente (DDT complet)", icon: "ri-home-2-line", price: "Dès 280 €" },
  { value: "pack-location", label: "Pack Location", icon: "ri-key-2-line", price: "Dès 180 €" },
  { value: "amiante", label: "Diagnostic Amiante", icon: "ri-shield-check-line", price: "Dès 120 €" },
  { value: "plomb", label: "Plomb (CREP)", icon: "ri-test-tube-line", price: "Dès 110 €" },
  { value: "electricite", label: "Diagnostic Électricité", icon: "ri-flashlight-line", price: "Dès 100 €" },
  { value: "gaz", label: "Diagnostic Gaz", icon: "ri-gas-station-line", price: "Dès 100 €" },
  { value: "termites", label: "Diagnostic Termites", icon: "ri-bug-line", price: "Dès 90 €" },
  { value: "loi-carrez", label: "Mesurage Loi Carrez", icon: "ri-ruler-2-line", price: "Dès 80 €" },
];

const steps = ["Type de bien", "Diagnostics", "Votre bien", "Coordonnées"];

export default function DevisPage() {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    type: "",
    diagnostics: [] as string[],
    surface: "",
    annee: "",
    etage: "",
    ville: "",
    codePostal: "",
    motif: "vente",
    nom: "",
    email: "",
    telephone: "",
    message: "",
  });

  const toggleDiag = (val: string) => {
    setFormData((prev) => ({
      ...prev,
      diagnostics: prev.diagnostics.includes(val)
        ? prev.diagnostics.filter((d) => d !== val)
        : [...prev.diagnostics, val],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append("type", formData.type);
      body.append("diagnostics", formData.diagnostics.join(", "));
      body.append("surface", formData.surface);
      body.append("annee", formData.annee);
      body.append("etage", formData.etage);
      body.append("ville", formData.ville);
      body.append("codePostal", formData.codePostal);
      body.append("motif", formData.motif);
      body.append("nom", formData.nom);
      body.append("email", formData.email);
      body.append("telephone", formData.telephone);
      body.append("message", formData.message);
      await fetch("https://readdy.ai/api/form/d7evamqa509fjl5l00i0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <title>Devis Gratuit Diagnostic Immobilier Paris & IDF — Réponse en 2h | Up Diag</title>
      <meta name="description" content="Obtenez votre devis gratuit pour un diagnostic immobilier à Paris et en Île-de-France. DPE, Amiante, Plomb, Pack Vente. Réponse en 2h, intervention sous 48h." />
      <TopBar />
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#0F3B5F] to-[#0a2d4a] py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <span className="inline-block bg-white/10 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
              Devis gratuit
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
              Obtenez votre devis en 2 minutes
            </h1>
            <p className="text-lg text-white/80 max-w-xl mx-auto">
              Gratuit, sans engagement. Réponse sous 2h, intervention sous 48h dans toute l'Île-de-France.
            </p>
          </div>
        </section>

        {/* Form section */}
        <section className="bg-[#F8FAFC] py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              {/* Left — Form */}
              <div className="lg:col-span-2">
                {submitted ? (
                  <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <i className="ri-checkbox-circle-fill text-[#16A34A] text-4xl"></i>
                    </div>
                    <h2 className="text-2xl font-extrabold text-[#0F3B5F] mb-3" style={{ fontFamily: "Barlow, sans-serif" }}>
                      Demande envoyée avec succès !
                    </h2>
                    <p className="text-gray-500 mb-2">Nous avons bien reçu votre demande de devis.</p>
                    <p className="text-gray-500 mb-8">Notre équipe vous contacte sous <strong>2h</strong> avec votre devis personnalisé.</p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Link to="/" className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#0F3B5F] text-[#0F3B5F] rounded-xl font-bold hover:bg-[#0F3B5F] hover:text-white transition-all whitespace-nowrap">
                        Retour à l'accueil
                      </Link>
                      <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="flex items-center justify-center gap-2 px-6 py-3 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap">
                        <i className="ri-phone-fill"></i> Appeler maintenant
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    {/* Stepper */}
                    <div className="bg-[#0F3B5F]/5 px-8 py-5 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        {steps.map((s, i) => (
                          <div key={s} className="flex items-center gap-2 flex-1">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all duration-300 ${
                              i < step ? "bg-[#16A34A] text-white" : i === step ? "bg-[#F97316] text-white" : "bg-gray-200 text-gray-400"
                            }`}>
                              {i < step ? <i className="ri-check-line text-sm"></i> : i + 1}
                            </div>
                            <span className={`text-xs font-medium hidden sm:block whitespace-nowrap ${i === step ? "text-[#0F3B5F]" : "text-gray-400"}`}>{s}</span>
                            {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i < step ? "bg-[#16A34A]" : "bg-gray-200"}`}></div>}
                          </div>
                        ))}
                      </div>
                    </div>

                    <form data-readdy-form onSubmit={handleSubmit} className="p-8">
                      {/* Step 0 — Type */}
                      {step === 0 && (
                        <div>
                          <h2 className="text-xl font-extrabold text-[#0F3B5F] mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>Type de bien</h2>
                          <p className="text-sm text-gray-500 mb-6">Quel type de bien souhaitez-vous faire diagnostiquer ?</p>
                          <div className="grid grid-cols-2 gap-4 mb-8">
                            {typeOptions.map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => setFormData({ ...formData, type: opt.value })}
                                className={`flex items-center gap-4 p-5 rounded-xl border-2 transition-all duration-200 cursor-pointer text-left ${
                                  formData.type === opt.value ? "border-[#F97316] bg-orange-50" : "border-gray-200 hover:border-gray-300"
                                }`}
                              >
                                <div className={`w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 ${formData.type === opt.value ? "bg-[#F97316] text-white" : "bg-gray-100 text-gray-500"}`}>
                                  <i className={`${opt.icon} text-2xl`}></i>
                                </div>
                                <div>
                                  <div className="font-bold text-[#0F3B5F]">{opt.label}</div>
                                </div>
                                {formData.type === opt.value && <i className="ri-checkbox-circle-fill text-[#F97316] ml-auto text-xl"></i>}
                              </button>
                            ))}
                          </div>
                          <button
                            type="button"
                            disabled={!formData.type}
                            onClick={() => setStep(1)}
                            className="w-full py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            Continuer <i className="ri-arrow-right-line ml-1"></i>
                          </button>
                        </div>
                      )}

                      {/* Step 1 — Diagnostics */}
                      {step === 1 && (
                        <div>
                          <h2 className="text-xl font-extrabold text-[#0F3B5F] mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>Diagnostics souhaités</h2>
                          <p className="text-sm text-gray-500 mb-6">Sélectionnez un ou plusieurs diagnostics (plusieurs choix possibles)</p>
                          <div className="flex flex-col gap-3 mb-8">
                            {diagnosticOptions.map((opt) => {
                              const selected = formData.diagnostics.includes(opt.value);
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => toggleDiag(opt.value)}
                                  className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer text-left ${
                                    selected ? "border-[#F97316] bg-orange-50" : "border-gray-200 hover:border-gray-300"
                                  }`}
                                >
                                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${selected ? "bg-[#F97316] text-white" : "bg-gray-100 text-gray-500"}`}>
                                    <i className={`${opt.icon} text-xl`}></i>
                                  </div>
                                  <span className="font-semibold text-[#0F3B5F] flex-1">{opt.label}</span>
                                  <span className="text-sm font-bold text-[#0F3B5F] whitespace-nowrap">{opt.price}</span>
                                  {selected && <i className="ri-checkbox-circle-fill text-[#F97316] text-xl flex-shrink-0"></i>}
                                </button>
                              );
                            })}
                          </div>
                          <div className="flex gap-3">
                            <button type="button" onClick={() => setStep(0)} className="px-5 py-4 border-2 border-gray-200 text-gray-600 rounded-xl font-semibold hover:border-gray-300 transition-all whitespace-nowrap">
                              <i className="ri-arrow-left-line"></i>
                            </button>
                            <button
                              type="button"
                              disabled={formData.diagnostics.length === 0}
                              onClick={() => setStep(2)}
                              className="flex-1 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                              Continuer <i className="ri-arrow-right-line ml-1"></i>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Step 2 — Bien */}
                      {step === 2 && (
                        <div>
                          <h2 className="text-xl font-extrabold text-[#0F3B5F] mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>Votre bien</h2>
                          <p className="text-sm text-gray-500 mb-6">Informations sur le bien à diagnostiquer</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
                            <div>
                              <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Surface (m²) *</label>
                              <input type="number" name="surface" placeholder="Ex: 65" value={formData.surface} onChange={(e) => setFormData({ ...formData, surface: e.target.value })}
                                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" required />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Année de construction *</label>
                              <input type="number" name="annee" placeholder="Ex: 1975" value={formData.annee} onChange={(e) => setFormData({ ...formData, annee: e.target.value })}
                                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" required />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Ville / Arrondissement *</label>
                              <input type="text" name="ville" placeholder="Ex: Paris 15e, Boulogne..." value={formData.ville} onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" required />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Code postal *</label>
                              <input type="text" name="codePostal" placeholder="Ex: 75015" value={formData.codePostal} onChange={(e) => setFormData({ ...formData, codePostal: e.target.value })}
                                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" required />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Étage (si appartement)</label>
                              <input type="text" name="etage" placeholder="Ex: 3e étage" value={formData.etage} onChange={(e) => setFormData({ ...formData, etage: e.target.value })}
                                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Motif</label>
                              <select name="motif" value={formData.motif} onChange={(e) => setFormData({ ...formData, motif: e.target.value })}
                                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors">
                                <option value="vente">Vente</option>
                                <option value="location">Location</option>
                                <option value="travaux">Avant travaux</option>
                                <option value="autre">Autre</option>
                              </select>
                            </div>
                          </div>
                          <div className="flex gap-3">
                            <button type="button" onClick={() => setStep(1)} className="px-5 py-4 border-2 border-gray-200 text-gray-600 rounded-xl font-semibold hover:border-gray-300 transition-all whitespace-nowrap">
                              <i className="ri-arrow-left-line"></i>
                            </button>
                            <button
                              type="button"
                              disabled={!formData.surface || !formData.annee || !formData.ville || !formData.codePostal}
                              onClick={() => setStep(3)}
                              className="flex-1 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                              Continuer <i className="ri-arrow-right-line ml-1"></i>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Step 3 — Contact */}
                      {step === 3 && (
                        <div>
                          <h2 className="text-xl font-extrabold text-[#0F3B5F] mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>Vos coordonnées</h2>
                          <p className="text-sm text-gray-500 mb-6">Pour recevoir votre devis personnalisé sous 2h</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                            <div className="sm:col-span-2">
                              <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Nom complet *</label>
                              <input type="text" name="nom" placeholder="Jean Dupont" value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" required />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Email *</label>
                              <input type="email" name="email" placeholder="jean@exemple.fr" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" required />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Téléphone</label>
                              <input type="tel" name="telephone" placeholder="06 XX XX XX XX" value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" />
                            </div>
                            <div className="sm:col-span-2">
                              <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Message (optionnel)</label>
                              <textarea name="message" placeholder="Précisions sur votre bien, contraintes horaires..." value={formData.message}
                                onChange={(e) => { if (e.target.value.length <= 500) setFormData({ ...formData, message: e.target.value }); }}
                                rows={3} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors resize-none" />
                              <p className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</p>
                            </div>
                          </div>
                          <div className="flex gap-3">
                            <button type="button" onClick={() => setStep(2)} className="px-5 py-4 border-2 border-gray-200 text-gray-600 rounded-xl font-semibold hover:border-gray-300 transition-all whitespace-nowrap">
                              <i className="ri-arrow-left-line"></i>
                            </button>
                            <button
                              type="submit"
                              disabled={submitting || !formData.nom || !formData.email}
                              className="flex-1 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all disabled:opacity-50 whitespace-nowrap"
                            >
                              {submitting ? "Envoi en cours..." : "Obtenir mon devis gratuit"}
                              {!submitting && <i className="ri-arrow-right-line ml-1"></i>}
                            </button>
                          </div>
                          <p className="text-[11px] text-gray-400 text-center mt-3">Réponse sous 2h — Aucun engagement — 100% gratuit</p>
                        </div>
                      )}
                    </form>
                  </div>
                )}
              </div>

              {/* Right — Sidebar */}
              <div className="flex flex-col gap-5">
                {/* Récap */}
                {(formData.type || formData.diagnostics.length > 0) && !submitted && (
                  <div className="bg-white rounded-2xl p-6 border border-orange-100">
                    <h3 className="font-bold text-[#0F3B5F] mb-4 text-sm" style={{ fontFamily: "Barlow, sans-serif" }}>Votre sélection</h3>
                    {formData.type && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                        <i className="ri-home-2-line text-[#F97316]"></i>
                        <span className="capitalize">{formData.type}</span>
                      </div>
                    )}
                    {formData.diagnostics.map((d) => {
                      const opt = diagnosticOptions.find((o) => o.value === d);
                      return opt ? (
                        <div key={d} className="flex items-center justify-between text-sm mb-1.5">
                          <span className="text-gray-600">{opt.label.split(" — ")[0]}</span>
                          <span className="font-bold text-[#0F3B5F]">{opt.price}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                )}

                {/* Garanties */}
                <div className="bg-white rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-bold text-[#0F3B5F] mb-4 text-sm" style={{ fontFamily: "Barlow, sans-serif" }}>Nos garanties</h3>
                  <div className="flex flex-col gap-3">
                    {[
                      { icon: "ri-checkbox-circle-fill", text: "Certifié COFRAC", color: "text-[#16A34A]" },
                      { icon: "ri-timer-flash-fill", text: "RDV sous 48h", color: "text-[#F97316]" },
                      { icon: "ri-file-check-line", text: "Rapport le jour même", color: "text-[#0F3B5F]" },
                      { icon: "ri-price-tag-3-fill", text: "Tarif fixe, sans surprise", color: "text-[#F97316]" },
                      { icon: "ri-shield-check-fill", text: "RC Pro assurée", color: "text-[#16A34A]" },
                    ].map((item) => (
                      <div key={item.text} className="flex items-center gap-3 text-sm text-gray-600">
                        <i className={`${item.icon} ${item.color} text-lg flex-shrink-0`}></i>
                        {item.text}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Téléphone */}
                <div className="bg-[#0F3B5F] rounded-2xl p-6 text-white">
                  <h3 className="font-bold mb-2 text-sm" style={{ fontFamily: "Barlow, sans-serif" }}>Préférez-vous appeler ?</h3>
                  <p className="text-white/70 text-xs mb-4">Nos conseillers sont disponibles Lun–Sam 8h–19h</p>
                  <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="flex items-center justify-center gap-2 w-full py-3 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap text-sm">
                    <i className="ri-phone-fill"></i> {PHONE}
                  </a>
                </div>

                {/* Avis */}
                {SHOW_REVIEWS && (
                  <div className="bg-white rounded-2xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map((s) => <i key={s} className="ri-star-fill text-[#F59E0B] text-sm"></i>)}
                      </div>
                      <span className="font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>4.9/5</span>
                    </div>
                    <p className="text-xs text-gray-500">Basé sur 127 avis Google vérifiés</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
